/**
 * Governance approval cards (agion/Agion#2618). A runner marks the cards it
 * decides inline (governance_context.inline_decision): it resolves the card's
 * explicit decision for the signed-in person who answered, and governance
 * checks that person's decision right. Only such a card offers Approve and
 * Deny. A card without the mark comes from an older runner and is decided in
 * Escalations, where the browser's own session is checked; it links there
 * and never shows the escalation's identifier.
 */

import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { adminEscalationsHref } from '../../services/escalation-thread'
import type { HITLDecision } from '../../types/streaming'

/** An approval card's answer: the runner's option text and the explicit decision it stands for. */
export interface ApprovalAnswer {
  option: string
  decision: HITLDecision
}

export const APPROVE_ANSWER: ApprovalAnswer = { option: 'Approve', decision: 'approved' }
export const DENY_ANSWER: ApprovalAnswer = { option: 'Deny', decision: 'denied' }
export const APPROVAL_ANSWERS: ReadonlyArray<ApprovalAnswer> = [APPROVE_ANSWER, DENY_ANSWER]

/** How a governance card is decided: on the card, or in Escalations. */
export type ApprovalMode = 'inline' | 'escalations'

/**
 * A card's governance context as the chat holds it: camelCase from the live
 * stream (ActiveHITLRequest), snake_case on a replayed history row.
 */
type GovernanceMarks = object

function mark(governance: GovernanceMarks, camel: string, snake: string): unknown {
  const fields = governance as Record<string, unknown>
  return fields[camel] ?? fields[snake]
}

/** The card's mode, or null when the request is not a governance approval. */
export function approvalMode(governance: GovernanceMarks | null | undefined): ApprovalMode | null {
  if (!governance) return null
  return mark(governance, 'inlineDecision', 'inline_decision') === true ? 'inline' : 'escalations'
}

/** The escalation a card names, if any. */
export function approvalEscalationId(governance: GovernanceMarks | null | undefined): string | undefined {
  if (!governance) return undefined
  const id = mark(governance, 'escalationId', 'escalation_id')
  return typeof id === 'string' && id !== '' ? id : undefined
}

/** The admin console's Escalations page, opened on the escalation when the card names one. */
export function escalationsHref(escalationId?: string): string {
  return adminEscalationsHref(escalationId)
}

/** The note and link an approval card shows when it is decided in Escalations. */
export function DecideInEscalations({ href }: { href: string }) {
  const { t } = useTranslation(['common'])
  return (
    <div className="space-y-1 text-sm" data-testid="approval-decide-in-escalations">
      <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
        {t('common:hitl.policyReview.decideElsewhere', 'This approval is decided in Escalations.')}
      </p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-h-[44px] items-center gap-1 font-medium text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
      >
        {t('common:hitl.policyReview.openEscalations', 'Decide in Escalations')}
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </div>
  )
}
